import React, { useEffect, useState, useRef } from "react";
  import './App.css';
  import Routing from './RouteConfig/RouteHandler';
  import { Provider } from 'react-redux';
  import { store, persistor } from './Redux/store';
  import { PersistGate } from 'redux-persist/integration/react';
  import 'bootstrap/dist/css/bootstrap.min.css';
  import i18n from './i18n';
  import { I18nextProvider } from 'react-i18next';
  import { ChatProvider } from './Components/Layout/ChatContext';
  import FloatingChatButton from './Components/Layout/floatingChatButton';
  import 'react-toastify/dist/ReactToastify.css';
  import { ToastContainer } from 'react-toastify';
  import { useSelector } from "react-redux";
  import branch from 'branch-sdk';
  const queryParams = new URLSearchParams(window.location.search);
  const isSSOCallback = queryParams.has("SSO_ID") && queryParams.has("SSO_PARAMS");
  const ssoRedirecting = sessionStorage.getItem("highmarkSession") === "true";
 
  function App() {
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [isStateRehydrated, setIsStateRehydrated] = useState(false);
    const [isHighmarkPortal, setIsHighmarkPortal] = useState(false);

    const hasShownPopup = useRef(false);
    
    const ssoData = useSelector((state) => state.sso.ssoUserData);
    const persistState = useSelector((state) => state._persist?.rehydrated);

    // Wait for Redux persist to complete rehydration
    useEffect(() => {
      if (persistState) {
        setIsStateRehydrated(true);
      }
    }, [persistState]);

    useEffect(() => {
      if (!ssoData) return;
      const fromRedux = ssoData?.Portal === "Highmark";
      const winFromRedux = ssoData?.Portal?.toLowerCase() === "win";
      if (winFromRedux) {
        setIsHighmarkPortal(false);
      } else {
        setIsHighmarkPortal(!!fromRedux);
      }
    }, [ssoData]);
    
    useEffect(() => {
      const loadStyle = (href, id) => {
        if (document.getElementById(id)) return;
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = `${href}?v=${Date.now()}`;
        document.head.appendChild(link);
      };
      const removeStyle = (id) => {
        const existing = document.getElementById(id);
        if (existing) existing.remove();
      };
      const portalFolder = isHighmarkPortal ? "highmark" : "win"; //actual
      // const portalFolder = isHighmarkPortal ? "win" : "highmark";
      const styleFiles = [
        "PrepregnancyDetails","styles", "home", "login", "components", "header", "buttons",
        "modal", "calendar", "ErrorModal", "benefits", "video-consultation",
        "winpay", "wincentive", "weekly-tracker", "playvideo",  "CustomDropdown", "floatingchatstyles"
      ];

      styleFiles.forEach(removeStyle);
      styleFiles.forEach((fileName) => loadStyle(`/${portalFolder}/${fileName}.css`, fileName));
    }, [isHighmarkPortal]);    
    useEffect(() => {
      // Don't set title until state is rehydrated for SSO scenarios
      if (!isStateRehydrated && (isSSOCallback || ssoRedirecting)) {
        return;
      }

      // Set document title
      const ishighmarklogo = process.env.REACT_APP_IS_HIGHMARK === "true";
      const showHighmark = isHighmarkPortal || ishighmarklogo;

      if (showHighmark) {
        // For SSO scenarios, wait for domainInfo to be available before setting title
        const isSSOExpected = isSSOCallback || ssoRedirecting;
        const isDomainInfoPending = isSSOExpected && !ssoData?.domainInfo;
        
        if (isDomainInfoPending) {
          return; // Wait for domainInfo before setting title
        }

        if (ssoData?.domainInfo === "allmyhealth") {
          document.title = "AllMyHealth";
          // Set favicon for 'All My Health'
          const favicon = document.querySelector("link[rel='icon']");
          if (favicon) {
            favicon.href = "/favicon_allmyhealth.png"; // Favicon for 'All My Health'
          }
        } else {
          document.title = "My Highmark";
          // Set favicon for 'Highmark'
          const favicon = document.querySelector("link[rel='icon']");
          if (favicon) {
            favicon.href = "/favicon_highmark.png"; // Favicon for 'Highmark'
          }
        }
      } else {
              document.title = showHighmark ? "My Highmark" : "WINFamily";

        
        // Set favicon
        const favicon = document.querySelector("link[rel='icon']");
        if (favicon) {
          favicon.href = "/favicon.ico"; // your default
        }
      }
    }, [isHighmarkPortal, ssoData, isStateRehydrated]);
    

    const checkDeploymentStatus = () => {
      fetch('/deploy-complete.txt', { cache: "no-store" })
        .then(response => {
          if (!response.ok) {
            setTimeout(checkDeploymentStatus, 15000);
            return;
          }

          response.text().then(content => {
            const currentDeployMarker = content.trim();
            const lastSeenDeploy = localStorage.getItem("lastSeenDeploy");
            const acknowledgedDeploy = sessionStorage.getItem("acknowledgedDeploy");
            const loginDeployVersion = sessionStorage.getItem("loginDeployVersion");
            const justLoggedInViaSSO = sessionStorage.getItem("ssoLoginCompleted") === "true";
            const isLoginPage = document.getElementById('isLoginPage')?.value === 'true';
            const isSessionExpirePage = document.getElementById('isSessionexpirepage')?.value === 'true';

            if (isLoginPage || isSessionExpirePage) return;

            // First load - initialize silently
            if (!lastSeenDeploy && !acknowledgedDeploy && !loginDeployVersion) {
              localStorage.setItem("lastSeenDeploy", currentDeployMarker);
              sessionStorage.setItem("acknowledgedDeploy", currentDeployMarker);
              sessionStorage.setItem("loginDeployVersion", currentDeployMarker);
              return;
            }

            // SSO login - initialize silently and skip popup
            if (justLoggedInViaSSO) {
              sessionStorage.removeItem("ssoLoginCompleted");
              localStorage.setItem("lastSeenDeploy", currentDeployMarker);
              sessionStorage.setItem("acknowledgedDeploy", currentDeployMarker);
              sessionStorage.setItem("loginDeployVersion", currentDeployMarker);
              return;
            }

            // Check conditions
            const isNewDeployWhileUserActive = currentDeployMarker !== lastSeenDeploy;
            const isAlreadyAcknowledged = acknowledgedDeploy === currentDeployMarker;
            const isLoggedInAfterDeploy = loginDeployVersion === currentDeployMarker;
            if (
              isNewDeployWhileUserActive &&
              !isAlreadyAcknowledged &&
              !isLoggedInAfterDeploy &&
              !hasShownPopup.current
            ) {
              hasShownPopup.current = true;

              setTimeout(() => {
                const isStillLoginOrSessionPage =
                  document.getElementById('isLoginPage')?.value === 'true' ||
                  document.getElementById('isSessionexpirepage')?.value === 'true';

                if (isStillLoginOrSessionPage) return;

                setShowUpdateModal(true);
                localStorage.setItem("lastSeenDeploy", currentDeployMarker);
                sessionStorage.setItem("acknowledgedDeploy", currentDeployMarker);
              }, 10000);
            }
          });
        })
        .catch(() => {
          setTimeout(checkDeploymentStatus, 15000);
        });
    };

    useEffect(() => {
      const interval = setInterval(checkDeploymentStatus, 30000);
      checkDeploymentStatus();
      return () => clearInterval(interval);
    }, []);

    useEffect(() => {
      const observer = setInterval(() => {
        const isLoginPage = document.getElementById('isLoginPage')?.value === 'true';
        const isSessionExpirePage = document.getElementById('isSessionexpirepage')?.value === 'true';
        if ((isLoginPage || isSessionExpirePage) && showUpdateModal) {
          setShowUpdateModal(false);
        }
      }, 500);

      return () => clearInterval(observer);
    }, [showUpdateModal]);
   
    // useEffect(() => {
    //   console.log('[App.js] Starting Branch initialization');
    //   branch.init('key_live_aFhXqy0CNUR22lnOcqB2RpgiEzbdVs9x', (err, data) => {
    //     if (err) {
    //       console.error('[App.js] Branch init error', err);
    //     } else {
    //       console.log('[App.js] Branch init success', data);
    //     }
    //   });
    // }, []);

    return (
      <div className="App">
        <PersistGate loading={null} persistor={persistor}>
          <ChatProvider>
            <I18nextProvider i18n={i18n}>
              <FloatingChatButton />
              <Routing />
            </I18nextProvider>
          </ChatProvider>
        </PersistGate>

        {showUpdateModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}>
            <div style={{
              backgroundColor: "#fff",
              padding: "30px",
              borderRadius: "15px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
              width: "90%",
              maxWidth: "400px",
              textAlign: "center",
              animation: "fadeIn 0.3s ease",
            }}>
              <h3 className="text-primary-color">
                🚨 New Version Available!
              </h3>
              <p className="mb-5">
                We've deployed some updates. Click below to refresh the app and get the latest features.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="win-btn-primary"
              >
                Refresh Now 🚀
              </button>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    );
  }

  export default App;
